import { CheckCircle, CircleAlert, Info, Loader, XCircle } from 'lucide-react';
import posthog from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { StrictMode, Suspense, useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { Toaster, ToasterProps } from 'sonner';
import ResponsiveDialogProvider from 'ui/components/ResponsiveDialog/responsive-dialog-provider';
import { TooltipProvider } from 'ui/components/Tooltip';
import AppContent from './App';
import AppLoader from './AppLoader';
import i18n from './i18n';
import { store } from './store';

const toasterProps: ToasterProps = {
    toastOptions: {
        classNames: {
        },
        className: "pointer-events-auto",
    },
    position: 'top-right',
    icons: {
        success: <CheckCircle size={20} className="text-ata-green" />,
        error: <XCircle size={20} className="text-ata-pink" />,
        warning: <CircleAlert size={20} className="text-ata-orange" />,
        info: <Info size={20} className="text-ata-light-blue" />,
        loading: <Loader size={20} className="text-ata-purple" />,
    },
};


// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
    return (
        <div role="alert" style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Something went wrong:</h2>
            <pre style={{ color: 'red' }}>{error.message}</pre>
            <button onClick={resetErrorBoundary}>Try again</button>
        </div>
    );
};

// This component includes all providers
const AppWrapper = () => {
    const posthogClient = usePostHog();

    useEffect(() => {
        const environment = import.meta.env.MODE || 'development';
        if (posthogClient && !posthogClient.has_opted_in_capturing()) {
            try {
                const apiKey = import.meta.env.VITE_APP_POSTHOG_PROJECT_API_KEY;

                if (!apiKey) {
                    throw new Error("PostHog initialization failed: VITE_APP_POSTHOG_PROJECT_API_KEY is missing.");
                }

                posthogClient.init(apiKey, {
                    api_host: import.meta.env.VITE_APP_POSTHOG_API_HOST || 'https://hedgehog.atarimworker.io',
                    loaded: (posthog) => {
                        posthog.setPersonProperties({
                            environment,
                        });
                    }
                });

            } catch (error) {
                console.error("🔥🔥 POSTHOG INIT FAILED 🔥🔥");
                console.error("This did NOT block the app from running, but analytics will not be captured.");
                console.error(error);
            }
        }
    }, [posthogClient]);

    return (
        <StrictMode>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
                <PostHogProvider client={posthog}>
                    <Suspense fallback={<AppLoader />}>
                        <I18nextProvider i18n={i18n} defaultNS={'collaborate'}>
                            <Provider store={store}>
                                <Toaster {...toasterProps} className='pointer-events-auto z-[2147483647]' expand />
                                <TooltipProvider delayDuration={100}>
                                    <ResponsiveDialogProvider>
                                        <Suspense fallback={<AppLoader />}>
                                            <AppContent />
                                        </Suspense>
                                    </ResponsiveDialogProvider>
                                </TooltipProvider>
                            </Provider>
                        </I18nextProvider>
                    </Suspense>
                </PostHogProvider>
            </ErrorBoundary>
        </StrictMode>
    );
};

export default AppWrapper;